/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */
import React, { useState, Fragment, useId, useEffect } from 'react';
import { Listbox, Switch, Transition } from '@headlessui/react';
import { dataService } from '../../../../../../services/dataService';
import { useSelector } from 'react-redux';
import { liveRegistrationType } from '../../../../../../CommonComponent/staticValues';
import { buyerFees } from '../../../../../../CommonComponent/staticValues';
import { buyerFeeLabel, isBuyerFeeType } from '../../../../../../utils/buyerFeeUtils';
import { currencySymbolMapping } from '../../../../../../CommonComponent/staticValues';
import ToolTip from '../../../../../../CommonComponent/tooltip'
import { useLocation } from 'react-router-dom';
import disableFieldsBasedOnAuctionStatus from '../../../../disableFieldsBasedOnAuctionStatus';

export default function SetUpAuctionRules({auctionData,setAuctionData, setEditedAuctionData, edit}) {
    const profile = useSelector((state) => state.profile);
    const pricing = useSelector((state) => state.pricingSlice);
    const state = useLocation();
    const [registrationTypeShow, setRegistrationTypeShow] = useState(edit ? true : false)
    const [showFullContent, setShowFullContent] = useState(false);
    const [lastEditedVoluntaryIndex, setLastEditedVoluntaryIndex] = useState(null);
    const recommendedFeeGroupName = useId();

    const plan_status = pricing.plan_type;

    /**
     * The function `handleBuyerFeeValueChange` is used to handle changes in the buyer fee value in a
     * React component, ensuring that the value is valid and updating the state accordingly.
     */
    const handleBuyerFeeValueChange = (e, field) => {
        e.preventDefault();
        const newValue = e.target.value;
        let digitsOnly = newValue.replace(/^0*(\d*)/, '$1').replace(/\D/g, '');
        if (field === 'percentage') {
            if (digitsOnly !== '' && !isNaN(digitsOnly)) {
                digitsOnly = parseFloat(digitsOnly);
                digitsOnly = Math.min(100, Math.max(0, digitsOnly));
            }
        }
        const updatedData = {
            ...auctionData,
            [field]: digitsOnly,
        };
        setAuctionData(updatedData)
        setEditedAuctionData((prevData) => ({ ...prevData, [field]: digitsOnly }));

        if (field === 'percentage') {
            setAuctionData((prevData) => ({ ...prevData, fees: '' }));
            setEditedAuctionData((prevData) => ({ ...prevData, fees: '' }));
        }
        if (field === 'fees') {
            setAuctionData((prevData) => ({ ...prevData, percentage: '' }));
            setEditedAuctionData((prevData) => ({ ...prevData, percentage: '' }));
        }
    }

    const handleVoluntaryContributionChange = (index, field, rawValue) => {
        if (field === 'value') {
            setLastEditedVoluntaryIndex(index);
        }

        const normalizeOptionValue = (value) => (value === null || value === undefined ? '' : String(value));

        if (field === 'is_recommended') {
            const currentOptions = auctionData?.buyer_fee_details?.percentage_options || [];
            const targetValue = normalizeOptionValue(currentOptions[index]?.value);
            if (targetValue.trim() === '') {
                return;
            }
        }

        const buildBuyerFeeDetails = (currentBuyerFeeDetails = {}) => {
            const existingOptions = Array.isArray(currentBuyerFeeDetails?.percentage_options)
                ? currentBuyerFeeDetails.percentage_options
                : [];

            const percentageOptions = [0, 1, 2].map((optionIndex) => ({
                value: normalizeOptionValue(existingOptions?.[optionIndex]?.value),
                is_recommended: !!existingOptions?.[optionIndex]?.is_recommended,
            }));

            if (field === 'value') {
                let digitsOnly = String(rawValue || '').replace(/\D/g, '');
                if (digitsOnly !== '' && !isNaN(digitsOnly)) {
                    const parsedValue = parseInt(digitsOnly, 10);
                    if (!isNaN(parsedValue)) {
                        const clampedValue = Math.min(100, parsedValue);
                        digitsOnly = clampedValue < 1 ? '' : clampedValue.toString();
                    }
                }

                percentageOptions[index] = {
                    ...(percentageOptions[index] || {}),
                    value: digitsOnly,
                };
            }

            if (field === 'is_recommended') {
                percentageOptions.forEach((option, optionIndex) => {
                    percentageOptions[optionIndex] = {
                        ...(option || {}),
                        is_recommended: optionIndex === index,
                    };
                });
            }

            return {
                ...(currentBuyerFeeDetails || {}),
                fee_type: 'voluntary_contribution',
                percentage_options: percentageOptions,
            };
        };

        const getRecommendedPercentage = (buyerFeeDetails) => {
            const recommendedOption = buyerFeeDetails?.percentage_options?.find((option) => option.is_recommended);
            return recommendedOption?.value || '';
        };

        const updatedDetails = buildBuyerFeeDetails(auctionData?.buyer_fee_details);

        setAuctionData((prevData) => ({
            ...prevData,
            buyer_fee_details: updatedDetails,
            percentage: getRecommendedPercentage(updatedDetails),
        }));

        setEditedAuctionData((prevData) => ({
            ...prevData,
            buyer_fee_details: updatedDetails,
            percentage: getRecommendedPercentage(updatedDetails),
        }));
    }

    const content = (<div>
        <p>
            Choose between three ways to end your auction.
            All Lots - All lots close at the same set time.
        </p>
        {showFullContent ? (
            <button className='text-left' onClick={() => setShowFullContent(false)}>
                As bidding is extended on all lots,
                underbidders can bid again on any lot in the auction so this style of closing is useful
                for auctions where bidders may be interested in more than one lot.
                Cascade - Each lot closes at pre-set intervals only after the previous lot has closed.
                If a bid is made during the last minute of a lot closing, the auction for that lot will extend.
                Since lots close consecutively, a bidding extension will delay the closing of the next lot,
                ensuring only one lot closes at a time.
                Each lot closes at a specified closing time, two minutes apart. A bid placed within the last
                minute of a lot closing will only extend the bidding on that lot. The added time will not affect
                the closing time of any other lots, so the lots may close out of numerical order. This style of
                closing is ideal for large auctions with over 100 lots as bidding extensions apply only to lots
                with active bidders. Show Less...
            </button>
        ) : (
            <button onClick={() => setShowFullContent(true)}>Show More...</button>
        )}
    </div>
    );
    /**
     * Function to return the disabled state 
     * from disabled fields maping based on it's
     * auction status
     * @param {*} field 
     * @returns 
     */
    const returnDisableState = (field) => {
        let statusCheck = false;
        let status = auctionData?.status ? auctionData.status : "";
        if (status)
            status = status !== "Accepting bids" ? status.toLowerCase() : "acceptingBids"
        switch (field) {
        case "registration_type": {
            statusCheck = disableFieldsBasedOnAuctionStatus[status]?.registration_type;
            break;
        }
        case "add_buyer_fees": {
            statusCheck = disableFieldsBasedOnAuctionStatus[status]?.add_buyer_fees;
            break;
        }
        default:
            break;
        }
        return statusCheck;
    }

    useEffect(() => {
        if (!isBuyerFeeType(auctionData?.add_buyer_fees, 'voluntary')) {
            return;
        }

        const existingOptions = Array.isArray(auctionData?.buyer_fee_details?.percentage_options)
            ? auctionData.buyer_fee_details.percentage_options
            : [];

        const normalizedOptions = [0, 1, 2].map((optionIndex) => ({
            value: existingOptions?.[optionIndex]?.value || '',
            is_recommended: !!existingOptions?.[optionIndex]?.is_recommended,
        }));

        const hasAnyValue = normalizedOptions.some((option) => option.value !== '');
        const hasRecommended = normalizedOptions.some((option) => option.is_recommended);

        if (hasAnyValue || hasRecommended) {
            return;
        }

        const defaultOptions = normalizedOptions.map((option, optionIndex) => ({
            ...option,
            is_recommended: optionIndex === 0,
        }));

        setAuctionData((prevData) => ({
            ...prevData,
            buyer_fee_details: {
                ...(prevData?.buyer_fee_details || {}),
                fee_type: 'voluntary_contribution',
                percentage_options: defaultOptions,
            },
            percentage: defaultOptions[0]?.value || '',
        }));

        setEditedAuctionData((prevData) => ({
            ...prevData,
            buyer_fee_details: {
                ...(prevData?.buyer_fee_details || {}),
                fee_type: 'voluntary_contribution',
                percentage_options: defaultOptions,
            },
            percentage: defaultOptions[0]?.value || '',
        }));
    }, [auctionData?.add_buyer_fees, auctionData?.buyer_fee_details?.percentage_options, setAuctionData, setEditedAuctionData]);


    return (
        <div className="auction-rule-content">
            <div className={`flex items-center pt-4 gap-2`}>
                <Switch
                    checked={auctionData?.accept_absentee_bid}
                    onChange={(value) => {
                        setAuctionData(prevData => ({
                            ...prevData,
                            accept_absentee_bid: value,
                        })), setEditedAuctionData(prevData => ({
                            ...prevData,
                            accept_absentee_bid: value,
                        }))
                    }}
                    data-testid="accept-absentee-bid"
                    className="border w-[90px] h-[26px] flex items-center 
                            justify-between border-[#A1A1A9] rounded-[54px] relative text-[10px] 
                            font-medium leading-normal px-2"
                >
                    Yes
                    <span
                        className={`${auctionData?.accept_absentee_bid ? '-translate-x-6' : 'translate-x-5'
                        } text-[10px] font-medium leading-normal rounded-xl min-w-[36px] cursor-pointer h-5 flex justify-center 
                                items-center bg-[#343434] text-[#FFFFFF]
                                m-0.5 text-center`}
                    >{auctionData?.accept_absentee_bid ? 'Yes' : 'No'}</span>
                    No
                </Switch>
                <p className="font-medium text-[#343434] text-[10px] leading-normal">Accept Absentee bids</p>
                <ToolTip trigger="click"
                    content="Bidders can leave an absentee bid in lieu of bidding from the room."
                    tooltipTestId="absentee-tooltip-content">
                    <img src="/images/info-icon.svg" className="cursor-pointer"
                        data-testid='absentee-tooltip-icon' alt="info-icon" loading="lazy" />
                </ToolTip>
            </div>
            <div className={`flex items-center pt-4 gap-2`}>
                <Switch
                    checked={auctionData?.accept_telephone_bid}
                    onChange={(value) => {
                        setAuctionData(prevData => ({
                            ...prevData,
                            accept_telephone_bid: value,
                        })), setEditedAuctionData(prevData => ({
                            ...prevData,
                            accept_telephone_bid: value,
                        }))
                    }}
                    data-testid="accept-telephone-bid"
                    className="border w-[90px] h-[26px] flex items-center 
                            justify-between border-[#A1A1A9] rounded-[54px] relative text-[10px] 
                            font-medium leading-normal px-2"
                >
                    Yes
                    <span
                        className={`${auctionData?.accept_telephone_bid ? '-translate-x-6' : 'translate-x-5'
                        } text-[10px] font-medium leading-normal rounded-xl min-w-[36px] cursor-pointer h-5 flex justify-center 
                                items-center bg-[#343434] text-[#FFFFFF]
                                m-0.5 text-center`}
                    >{auctionData?.accept_telephone_bid ? 'Yes' : 'No'}</span>
                    No
                </Switch>
                <p className="font-medium text-[#343434] text-[10px] leading-normal">Accept Telephone bids</p>
                <ToolTip trigger="click"
                    content="Bidders can set up a telephone bid if they are unable to attend the live auction. They are asked to leave an emergency bid amount in case you're unable to connect."
                    tooltipTestId="telephone-tooltip-content">
                    <img src="/images/info-icon.svg" className="cursor-pointer"
                        data-testid='telephone-tooltip-icon' alt="info-icon" loading="lazy" />
                </ToolTip>
            </div>
            {/* <!-- dropdown for registration type--> */}
            <Listbox as="div" className="relative w-full"
                disabled={returnDisableState("registration_type")}
                value={auctionData.registration_type}
                onChange={(value) => {
                    setRegistrationTypeShow(true)
                    setAuctionData(prevData => ({ ...prevData, registration_type: value.name }))
                    setEditedAuctionData(prevData => ({ ...prevData, registration_type: value.name }))
                }}>
                {({ open }) => (
                    <>
                        <div className="flex items-center mt-4 gap-2">
                            <p className="text-[12px] font-medium leading-normal text-[#343434]">Registration type</p>
                            <ToolTip trigger="click"
                                content="Choose your bidder registration requirements. Bidders can register with just their email, 
                        you can require a bank card or manually approve each one.">
                                <img src="/images/info-icon.svg" className="cursor-pointer" alt="info-icon" loading="lazy" />
                            </ToolTip>
                        </div>
                        <Listbox.Button type="button"
                            className={`text-[14px] text-start relative w-full h-11 px-3 border 
                            disabled:border-[#c9c9cc] disabled:text-[#A1A1A9] disabled:bg-[#F4F4F5]
                            border-[#A1A1A9] ${registrationTypeShow ? 'text-[#343434]' : 'text-[#A1A1A9]'} 
                            outline-none font-medium leading-normal`} id="registration-type">
                            <ToolTip content={auctionData.registration_type === '' ? 'Select' : auctionData.registration_type}>
                                <span className="truncate block pr-6">
                                    {auctionData.registration_type === '' ? 'Select' : auctionData.registration_type}
                                </span>
                            </ToolTip>
                        </Listbox.Button>
                        <img src="/images/dropdown-icon.png" alt="dropdown-icon" className="absolute top-[40px] right-[15px]"
                            loading="lazy" />
                        <Listbox.Options
                            className="bg-white rounded-[8px] z-30 absolute w-full [&[data-te-dropdown-show]]:block 
                shadow-bs1 list-none" aria-labelledby="extension-type">
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="pr-[15px] py-2 max-h-[120px] customScrollbar overflow-y-auto overflow-x-hidden">
                                    {liveRegistrationType.map((registrationType) => (
                                        <Listbox.Option key={registrationType.id} value={registrationType}
                                            disabled={(pricing.free_user === true || pricing.plan_type === 'Free' ||
                                                pricing.plan_type === 'Starter') &&
                                                (registrationType.name !== 'Email only')}
                                            as="div" className="pl-2 text-[12px] py-1
                                                leading-normal font-medium text-[#A1A1A9] list-none">
                                            {({ active, selected, disabled }) => (
                                                <li className={`py-1 -mt-0.5 rounded-[11px] px-2.5 
                                                ${!disabled && active ? 'bg-[#E6E6E6] cursor-pointer' :
                                                    `${disabled ? 'text-[#A1A1A9] cursor-auto bg-white' :
                                                        'text-[#343434] cursor-pointer bg-white'}`
                                                }`}>
                                                    <ToolTip content={registrationType.name}>
                                                        <span className="truncate block">
                                                            {registrationType.name}
                                                        </span>
                                                    </ToolTip>
                                                </li>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </div>
                            </Transition>
                        </Listbox.Options>
                    </>
                )}
            </Listbox>
            {/* <!-- dropdown for add buyer type--> */}
            <Listbox as="div"
                disabled={returnDisableState("add_buyer_fees")}
                className="relative w-full" value={auctionData.add_buyer_fees}
                onChange={(value) => {
                    setAuctionData((prevData) => {
                        const isSwitchingFromVoluntary = isBuyerFeeType(prevData?.add_buyer_fees, 'voluntary') &&
                            !isBuyerFeeType(value.key, 'voluntary');

                        return {
                            ...prevData,
                            add_buyer_fees: value.key,
                            ...(isSwitchingFromVoluntary ? { buyer_fee_details: {} } : {}),
                        };
                    });

                    setEditedAuctionData((prevData) => {
                        const isSwitchingFromVoluntary = isBuyerFeeType(prevData?.add_buyer_fees, 'voluntary') &&
                            !isBuyerFeeType(value.key, 'voluntary');

                        return {
                            ...prevData,
                            add_buyer_fees: value.key,
                            ...(isSwitchingFromVoluntary ? { buyer_fee_details: {} } : {}),
                        };
                    });
                }}>
                <div className="flex items-center mt-4 gap-2">
                    <p className="text-[12px] font-medium leading-normal text-[#343434]">Add buyer fees</p>
                    <ToolTip trigger="click"
                        content="You can add additional fees to the buyer either as a percentage of the 
                        winning bid price or as a flat fee.">
                        <img src="/images/info-icon.svg" className="cursor-pointer" alt="info-icon" loading="lazy" />
                    </ToolTip>
                </div>
                <Listbox.Button type="button"
                    className={`text-[14px] text-start relative w-full h-11 px-3 border 
                    disabled:border-[#c9c9cc] disabled:text-[#A1A1A9] disabled:bg-[#F4F4F5]
                    border-[#A1A1A9] ${auctionData.add_buyer_fees === '' ? 'text-[#A1A1A9]' : 'text-[#343434]'} 
                    outline-none font-medium leading-normal`} id="add_buyer_fee" data-testid="add_buyer_fee"
                    aria-expanded="false"
                    data-te-ripple-init
                    data-te-ripple-color="light">
                    <ToolTip content={auctionData.add_buyer_fees === '' ? 'Select' : buyerFeeLabel(auctionData.add_buyer_fees)}>
                        <span className="truncate block pr-6">
                            {auctionData.add_buyer_fees === '' ? 'Select' : buyerFeeLabel(auctionData.add_buyer_fees)}
                        </span>
                    </ToolTip>
                </Listbox.Button>
                <img src="/images/dropdown-icon.png" alt="dropdown-icon" className="absolute top-[40px] right-[15px]"
                    loading="lazy" />
                <Listbox.Options
                    className="bg-white rounded-[8px] z-30  absolute w-full [&[data-te-dropdown-show]]:block 
                shadow-bs1 list-none" aria-labelledby="add_buyer_fee"
                    data-te-dropdown-menu-ref>
                    <Transition
                        as={Fragment}
                        leave='transition ease-in duration-100'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'>
                        <div className="pr-[15px] py-2 max-h-[150px] customScrollbar overflow-y-auto overflow-x-hidden">
                            {buyerFees.map((fees, index) => (
                                <Listbox.Option key={fees.id} value={fees}
                                    disabled={(pricing.free_user === true || pricing.plan_type === 'Free') &&
                                        (fees.name !== 'No additional fees (default)')}
                                    as="div"
                                    className="pl-2 py-1 list-none
                                        text-[12px] leading-normal font-medium text-[#4A4A4A]">
                                    {({ active, selected, disabled }) => (
                                        <li className={`py-1 -mt-0.5 rounded-[11px] px-2.5 
                                        ${!disabled && active ? 'bg-[#E6E6E6] cursor-pointer' :
                                            `${disabled ? 'text-[#A1A1A9] cursor-auto bg-white' :
                                                'text-[#343434] cursor-pointer bg-white'}`
                                        }`}>
                                            <ToolTip content={`${fees.name}${fees.default ? ' (default)' : ''}`}>
                                                <span className="truncate block" data-testid={`${fees.name}`}>
                                                    {fees.name} {fees.default ? '(default)' : ''}
                                                </span>
                                            </ToolTip>
                                        </li>
                                    )}
                                </Listbox.Option>
                            ))}
                        </div>
                    </Transition>
                </Listbox.Options>
            </Listbox>
            {/* <!-- percentage --> */}
            {isBuyerFeeType(auctionData.add_buyer_fees, 'percentage') &&
                <div className="pt-3">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-[#343434] text-[12px] leading-normal">Percentage</p>
                        <ToolTip trigger="click"
                            content="You can add additional fees to the buyer either as a percentage of the 
                            winning bid price or as a flat fee.">
                            <img src="/images/info-icon.svg" className="cursor-pointer" alt="info-icon" loading="lazy" />
                        </ToolTip>
                    </div>
                    <input autoComplete='off' id="percentage-value" type="text"
                        disabled={auctionData.status == "Published" || auctionData.status == "Accepting bids"}
                        className="border border-[#A1A1A9] w-full px-3 py-3 outline-none 
                        disabled:border-[#c9c9cc] disabled:text-[#A1A1A9] disabled:bg-[#F4F4F5]
                text-[14px] leading-normal font-medium text-[#343434] placeholder:text-[#A1A1A9]"
                        value={auctionData.percentage === NaN ? '' : auctionData.percentage}
                        onChange={(e) => handleBuyerFeeValueChange(e, 'percentage')}
                        placeholder="%" />
                </div>
            }
            {/* <!-- fees --> */}
            {isBuyerFeeType(auctionData.add_buyer_fees, 'fixed') &&
                <div className="pt-3">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-[#343434] text-[12px] leading-normal">Fees</p>
                        <ToolTip trigger="click"
                            content="You can add additional fees to the buyer either as a percentage of the 
                        winning bid price or as a flat fee.">
                            <img src="/images/info-icon.svg" className="cursor-pointer" alt="info-icon" loading="lazy" />
                        </ToolTip>
                    </div>
                    <input autoComplete='off' id="fees" type="text"
                        disabled={auctionData.status == "Published" || auctionData.status == "Accepting bids"}
                        className="border border-[#A1A1A9] w-full px-3 py-3 outline-none text-[14px] 
                    disabled:border-[#c9c9cc] disabled:text-[#A1A1A9] disabled:bg-[#F4F4F5]
                leading-normal font-medium text-[#343434] placeholder:text-[#A1A1A9]"
                        value={auctionData.fees} onChange={(e) => handleBuyerFeeValueChange(e, 'fees')}
                        placeholder={currencySymbolMapping[auctionData.currency] || 'N/A'} />
                </div>}
            {/* <!-- voluntary contribution --> */}
            {isBuyerFeeType(auctionData.add_buyer_fees, 'voluntary') && (
                <div className="pt-3">
                    <p className="text-[#717179] text-[12px] leading-normal font-medium mb-4" data-testid="voluntary-contribution-tooltip">
                        Add up to 3 percentage fees to present to your buyers at checkout, and select the one you want to display as the recommended option.
                    </p>
                    <div className="flex justify-end mb-2">
                        <p className="text-[#717179] text-[10px] font-medium leading-normal mr-2">Recommended</p>
                    </div>
                    {[0, 1, 2].map((index) => {
                        const isBuyerFeeDisabled = returnDisableState("add_buyer_fees");
                        const val = auctionData?.buyer_fee_details?.percentage_options?.[index]?.value || '';
                        const isRecommended = !!auctionData?.buyer_fee_details?.percentage_options?.[index]?.is_recommended;
                        const isDuplicate = val !== '' && auctionData?.buyer_fee_details?.percentage_options?.some((opt, i) => i !== index && opt.value !== '' && opt.value === val);
                        const isDuplicateForEditedField = isDuplicate && lastEditedVoluntaryIndex === index;
                        const isError = (val === '' && isRecommended) || isDuplicateForEditedField;

                        return (
                            <div key={index} className="bg-[#F4F4F5] p-2.5 rounded-[8px] flex items-center gap-4 mb-3">
                                <div className="flex-1">
                                    <div className="relative">
                                        <span className="absolute pointer-events-none left-3 top-1/2 -translate-y-1/2 text-[#A1A1A9] text-[14px] font-medium">%</span>
                                        <input
                                            type="text"
                                            autoComplete='off'
                                            disabled={isBuyerFeeDisabled}
                                            className={`${isRecommended ? 'border-2' : 'border'} ${isError ? 'border-[#984848]' : 'border-[#282828]'} bg-white rounded-[4px] w-full pl-8 pr-3 py-2.5 outline-none text-[14px] leading-normal font-medium text-[#343434] placeholder:text-[#A1A1A9]`}
                                            value={val}
                                            onChange={(e) => handleVoluntaryContributionChange(index, 'value', e.target.value)}
                                        />
                                    </div>
                                    {isDuplicateForEditedField && (
                                        <p className="text-[10px] pt-1.5 font-medium leading-normal text-[#984848]">
                                            Please enter a different percentage value to the one you have already entered
                                        </p>
                                    )}
                                </div>
                                <div className="flex justify-center items-center pr-2">
                                    <input
                                        id={`recommended-fee-${recommendedFeeGroupName}-${index}`}
                                        type="radio"
                                        data-testid={`recommended-fee-${index}`}
                                        name={`recommended-fee-${recommendedFeeGroupName}`}
                                        className="w-6 h-6 accent-[#282828] cursor-pointer"
                                        disabled={isBuyerFeeDisabled}
                                        checked={isRecommended}
                                        onChange={() => handleVoluntaryContributionChange(index, 'is_recommended', true)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    )
}
